package one.healthium.data.user

import co.touchlab.kermit.Logger
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.EmailAuthProvider
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.functions.functions
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.shareIn
import kotlinx.coroutines.launch
import one.healthium.base.coroutines.IO
import one.healthium.data.user.storage.FirebaseAvatarStorage
import one.healthium.data.user.storage.FirebaseUserStorage
import one.healthium.data.user.storage.LocalUserStorage
import one.healthium.data.user.utils.SignInOptionsProvider
import one.healthium.domain.health_log.models.HealthLogOption
import one.healthium.domain.user.UserModel
import one.healthium.domain.user.UserRepository
import one.healthium.domain.user.isLocalId
import one.healthium.domain.user.models.FoodOptions
import one.healthium.domain.user.models.SignInOptions
import one.healthium.domain.user.models.SymptomsLogOptions
import one.healthium.domain.user.models.UserProfileModel
import one.healthium.domain.user.models.UserProfileOptions
import one.healthium.healthKit.data.HealthKitHelper
import kotlin.time.Instant

@OptIn(ExperimentalCoroutinesApi::class)
internal class AppUserRepository(
    private val localUserStorage: LocalUserStorage,
    private val remoteUserStorage: FirebaseUserStorage,
    private val signInOptionsProvider: SignInOptionsProvider,
    private val healthKitHelper: HealthKitHelper,
    private val avatarStorage: FirebaseAvatarStorage,
) : UserRepository {

    private val logger = Logger.withTag("AppUserRepository")

    private val auth = Firebase.auth
    private val functions = Firebase.functions

    private val scope = CoroutineScope(Dispatchers.IO + SupervisorJob())

    private val userFlow = localUserStorage.get()
        .map { user ->
            if (user == null) {
                logger.d("local user is null for some reasons, try to fetch Firebase user")
                val remoteUser = try {
                    tryFetchRemoteIfAuthenticated()
                } catch (e: Exception) {
                    logger.e("failed to fetch remote user", e)
                    null
                }
                if (remoteUser != null) {
                    localUserStorage.set(remoteUser)
                } else {
                    localUserStorage.set(UserModel.createDefault())
                }
            }

            user
        }
        .filterNotNull()
        .shareIn(scope, started = SharingStarted.Lazily, replay = 1)

    init {
        // monitor remote user
        scope.launch {
            localUserStorage.get()
                .distinctUntilChanged()
                .flatMapLatest { localUser ->
                    val userId = localUser?.id
                    if (userId != null && userId.isLocalId().not()) {
                        delay(1000) // in js target we have to wait for Firebase SDK initialization
                        logger.d("userId: $userId, fbUser: ${auth.currentUser?.uid}")
                        if (auth.currentUser == null) {
                            emptyFlow()
                        } else {
                            remoteUserStorage.get(userId)
                                .catch {
                                    logger.e("sync error, userId: $userId, fbUser: ${auth.currentUser?.uid}", it)
                                }
                        }
                    } else {
                        emptyFlow()
                    }
                }
                .filterNotNull()
                .catch {
                    logger.e("user watcher error", it)
                }
                .collect { user ->
                    logger.d("sync remote user: $user")
                    localUserStorage.set(user)
                }
        }
    }

    override suspend fun signIn(options: SignInOptions): UserModel {
        val result = when (options) {
            is SignInOptions.CustomToken -> auth.signInWithCustomToken(options.token)
            is SignInOptions.Username -> auth.signInWithCredential(EmailAuthProvider.credential(options.username, options.password))
        }
        logger.d("sign in result: ${result.user?.uid}")
        val userId = result.user?.uid
        if (userId != null) {
            val remoteUser = remoteUserStorage.get(userId).firstOrNull()
            logger.d("remote user: $remoteUser")
            return if (remoteUser != null) {
                notifyLogin(options)
                localUserStorage.set(remoteUser)
                remoteUser
            } else {
                val defaultUser = UserModel.createDefault(userId)
                localUserStorage.set(defaultUser)
                defaultUser
            }
        } else {
            throw Exception("user not found")
        }
    }

    override suspend fun silentSignIn(token: String): UserModel {
        val options = signInOptionsProvider.get(token) ?: throw Exception("user not found")
        return signIn(options)
    }

    override fun getUser(): Flow<UserModel> {
        return userFlow
    }

    override suspend fun updateUserProfile(data: UserProfileModel) {
        val userId = currentLocalUserId()
        if (userId != null) {
            localUserStorage.updateProfile(data)
            if (userId.isLocalId().not()) {
                remoteUserStorage.updateProfile(userId, data)
            }
        }
    }

    override suspend fun updateUserProfile(options: UserProfileOptions) {
        val userId = currentLocalUserId()
        if (userId != null) {
            if (userId.isLocalId().not()) {
                remoteUserStorage.updateProfile(userId, options)
            }
        }
    }

    override suspend fun addWaterIntake(value: Float) {
        val userId = currentLocalUserId()
        if (userId != null) {
            localUserStorage.addWaterIntake(value)
            if (userId.isLocalId().not()) {
                remoteUserStorage.addWaterIntake(userId, value)
            }
            healthKitHelper.logWater(value)
        }
    }

    override suspend fun addWeight(value: Float) {
        val userId = currentLocalUserId()
        if (userId != null) {
            localUserStorage.updateWeight(value)
            if (userId.isLocalId().not()) {
                remoteUserStorage.addWeight(userId, value)
            }
            healthKitHelper.logWeight(value)
        }
    }

    override suspend fun setWeightForDate(date: Instant, value: Float) {
        val userId = currentLocalUserId()
        if (userId != null) {
            localUserStorage.setWeightForDate(date, value)
            if (userId.isLocalId().not()) {
                remoteUserStorage.setWeightForDate(userId, date, value)
            }
        }
    }

    override suspend fun removeWeight(date: Instant) {
        val userId = currentLocalUserId()
        if (userId != null && userId.isLocalId().not()) {
            remoteUserStorage.removeWeight(userId, date)
        }
    }

    override suspend fun addFood(options: FoodOptions) {
        val userId = currentLocalUserId()
        if (userId != null && !userId.isLocalId()) {
            remoteUserStorage.addFood(userId, options)
        }
    }

    override suspend fun setFoodForDate(dayId: String, foodId: String, options: FoodOptions) {
        val userId = currentLocalUserId()
        if (userId != null && !userId.isLocalId()) {
            remoteUserStorage.setFoodForDate(userId, dayId, foodId, options)
        }
    }

    override suspend fun removeFood(dayId: String, foodId: String) {
        val userId = currentLocalUserId()
        if (userId != null && !userId.isLocalId()) {
            remoteUserStorage.removeFood(userId, dayId, foodId)
        }
    }

    override suspend fun setPeriodData(data: List<Instant>) {
        val userId = currentLocalUserId()
        if (userId != null && !userId.isLocalId()) {
            remoteUserStorage.setPeriods(userId, data)
        }
    }

    override suspend fun syncLocalToRemoteUser() {
        logger.d("sync local to remote user")
        val localUser = localUserStorage.get().firstOrNull()
        val remoteUserId = if (localUser == null || localUser.isLocalId) {
            getOrCreateRemoteUserId()
        } else {
            logger.d("sync local to remote user: skip, already synced")
            return
        }
        val remoteUser = if (localUser != null) {
            val updatedRemoteUser = remoteUserStorage.update(remoteUserId, localUser)
            if (localUser.profile.weight != null) {
                remoteUserStorage.addWeight(remoteUserId, localUser.profile.weight)
            }
            updatedRemoteUser
        } else {
            logger.d("no local user, no need to sync")
            remoteUserStorage.get(remoteUserId).firstOrNull()
        }
        logger.d("force sync remote user: $remoteUser")
        if (remoteUser != null) {
            localUserStorage.set(remoteUser)
        } else {
            val updatedLocalUser = localUser?.copy(id = remoteUserId) ?: UserModel.createDefault(remoteUserId)
            localUserStorage.set(updatedLocalUser)
        }
    }

    override suspend fun deleteAccount() {
        Firebase.functions.httpsCallable("deleteUser").invoke()
    }

    override suspend fun logout() {
        auth.signOut()
        localUserStorage.clear()
    }

    override suspend fun syncHealthData(fromDate: Instant) {
        syncWeight(fromDate)
    }

    override suspend fun updatePlayTime(status: Int) {
        val userId = currentLocalUserId()
        if (userId != null && userId.isLocalId().not()) {
            remoteUserStorage.updatePlayTime(userId, status)
        }
    }

    override suspend fun addSymptoms(options: SymptomsLogOptions) {
        val userId = currentLocalUserId()
        if (userId != null && userId.isLocalId().not()) {
            remoteUserStorage.addSymptoms(userId, options)
        }
    }

    override suspend fun addHealthLog(options: List<HealthLogOption>) {
        val userId = currentLocalUserId()
        if (userId != null && userId.isLocalId().not()) {
            remoteUserStorage.addHealthLog(userId, options)
        }
    }

    override suspend fun setAvatar(image: ByteArray) {
        val userId = currentLocalUserId()
        if (userId != null && userId.isLocalId().not()) {
            avatarStorage.uploadAvatar(userId, image).let { url ->
                remoteUserStorage.updateProfile(userId, UserProfileOptions(avatarUrl = url))
            }
        }
    }

    override suspend fun deleteAvatar() {
        val userId = currentLocalUserId()
        if (userId != null && userId.isLocalId().not()) {
            avatarStorage.deleteAvatar(userId)
            remoteUserStorage.updateProfile(userId, UserProfileOptions(avatarUrl = null))
        }
    }

    private suspend fun syncWeight(fromDate: Instant) {
        healthKitHelper.getWeightFromDate(fromDate).forEach { record ->
            setWeightForDate(record.date, record.weight)
        }
    }

    private suspend fun tryFetchRemoteIfAuthenticated(): UserModel? {
        try {
            val userId = auth.currentUser?.uid
            logger.d("authenticated userId: $userId")
            if (userId != null) {
                return remoteUserStorage.get(userId).firstOrNull()
            }
        } catch (e: Exception) {
            logger.e("failed sync user", e)
        }
        return null
    }

    private suspend fun currentLocalUserId(): String? {
        return localUserStorage.get().firstOrNull()?.id
    }

    private suspend fun getOrCreateRemoteUserId(): String {
        return auth.currentUser?.uid ?: auth.signInAnonymously().user?.uid ?: throw Exception("can't register user")
    }

    private fun notifyLogin(options: SignInOptions) {
        scope.launch {
            try {
                val email = when (options) {
                    is SignInOptions.CustomToken -> options.email
                    is SignInOptions.Username -> options.username
                }
                functions.httpsCallable("reminders_app_installed")
                    .invoke(mapOf("email" to email))
            } catch (e: Exception) {
                logger.e("failed to invoke reminders_app_installed", e)
            }
        }
    }
}
